import cloneDeep from 'lodash.clonedeep';
/**
 * Utilities for working with reducers in Kompis. Not meant to be used by
 * consumers.
 */
export default class ReducerUtility {
    /**
     * Utility function for keeping and modifying state using a reducer. Keeps
     * track of state internally and performes state reductions through the
     * returned `dispatch` function. Optionally takes a custom store object to
     * keep state in a different place.
     *
     * @param reducer Reducer function to use.
     * @param initialState Initial state that will be set as soon as `useReducer`
     * is called.
     * @param onStateChange Optional callback function that is triggered after a
     * reduction/state change has occurred.
     * @param store Optional store object that can be used to store the reducer
     * state in a different place. If not set `useReducer` will store the state
     * internally.
     * @returns An object with the current state and a function to dispatch
     * actions to the reducer.
     */
    static useReducer(reducer, initialState, onStateChange, store) {
        let _internalState;
        const _store = store ?? {
            get: () => _internalState,
            set: (state) => {
                _internalState = state;
            }
        };
        // Initialize store
        _store.set(cloneDeep(initialState));
        const accessor = {
            get state() {
                return _store.get();
            },
            set state(newState) {
                _store.set(newState);
            },
            dispatch
        };
        function dispatch(action) {
            const oldState = cloneDeep(accessor.state);
            accessor.state = reducer(accessor.state, action);
            onStateChange?.(oldState, accessor.state);
        }
        return accessor;
    }
    /**
     * Creates a copy of the reducer that emits events on the window object
     * whenever state changes to ease with debugging.
     *
     * @param reducer Reducer to wrap.
     * @returns Wrapped reducer function. Has the same API as the original reducer.
     */
    static exposeReducer(reducer) {
        // Wrapper function that dispatches events on the window object to make
        // debugging easier.
        const wrapperFunction = (state, action) => {
            const oldState = cloneDeep(state);
            const newState = reducer(state, action);
            window.dispatchEvent(ReducerUtility.createEventFromReduction(reducer, oldState, newState, action));
            return newState;
        };
        // Keep the name of the original function so that the wrapper function is
        // "hidden" from the consumer.
        Object.defineProperty(wrapperFunction, 'name', { value: reducer.name, writable: false });
        return wrapperFunction;
    }
    static createEventFromReduction(reducer, oldState, newState, action) {
        return new CustomEvent('$kompisReduction', {
            detail: {
                reducer: reducer.name,
                oldState,
                newState,
                action
            }
        });
    }
}
//# sourceMappingURL=ReducerUtility.js.map